//@ts-ignore
import HCLTreeMxGraph, {HCLTreeMxGraphJSON} from './HCLTreeMxGraph';
import EventTreeMxGraphDecoder from '../HCLTreeMxGraphDecoder/EventTreeMxGraphDecoder';
import EventTreeMxGraphEncoder from '../HCLTreeMxGraphEncoder/EventTreeMxGraphEncoder';
import EndStateVertex from '../HCLTreeNode/EventTreeNode/EndStateNode/EndStateVertex';
import FunctionalEventVertex from '../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex';
import InitVertex from '../HCLTreeNode/EventTreeNode/InitEvent/InitVertex';
import ESDMxGraphLayout from '../HCLTreeMxGraphLayout/EventTreeMxGraphLayout';
import EventTreeSuccessEdge from '../HCLTreeNode/EventTreeNode/EventTreeSuccessEdge';
import ReferenceTypes from "../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";
import {PathJSON} from "../HCLTreeNode/HCLTreeVertexValue/Path";
import {LabelJSON} from "../HCLTreeNode/HCLTreeVertexValue/Label";
import {OutcomeJSON} from "../HCLTreeNode/HCLTreeVertexValue/Outcome";
import ESDMxConnectionHandler from "./ConnectionHandler/ESDMxConnectionHandler";
import ESDMxStylesheet from "../Stylesheet/ESDMxStylesheet";
import ProxyTypes from "../HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import HCLTreeVertex from "../HCLTreeNode/HCLTreeVertex";
import FunctionalEventVertexValue from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertexValue";
import ESDMxCellRenderer from "../HCLCellRenderer/ESDMxCellRenderer";
import EventTreeVertex from "../HCLTreeNode/EventTreeNode/EventTreeVertex";
import FunctionalEventPort from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/Port/FunctionalEventPort";
import {
  mxCell, mxCellRenderer,
  mxCellState,
  mxConnectionHandler,
  mxConstants,
  mxGeometry,
  mxGraphLayout,
  mxRectangle, mxUtils
} from "mxgraph-js";
import ESDMxGraphModel from "../HCLMxGraphModel/ESDMxGraphModel";
import FaultTreeMxGraphSelectionModel from "../HCLMxGraphSelectionModel/FaultTreeMxGraphSelectionModel";
import ESDMxGraphSelectionModel from "../HCLMxGraphSelectionModel/ESDMxGraphSelectionModel";


interface EventTreeMxGraphJSON extends HCLTreeMxGraphJSON {
  [ReferenceTypes.BRANCHES]: {[index: string]: { outcome: BranchJSON }},
  [ReferenceTypes.FUNCTIONAL_EVENTS]: {[index: string]: { label: LabelJSON }},
  [ReferenceTypes.INITIAL_STATE]: {outcome?: OutcomeJSON},
  [ReferenceTypes.SEQUENCES]: {[index: string]: { label: LabelJSON }},
}

interface BranchJSON {
  [ReferenceTypes.FUNCTIONAL_EVENT]: string,
  [ReferenceTypes.PATHS]: {
    failure: PathJSON;
    success: PathJSON;
  },
  _proxy: ProxyTypes,
}

class EventTreeMxGraph extends HCLTreeMxGraph {

  private branchKeys: Set<string>;

  /**
   * @param {() => void} onGraphMounted
   * @param {() => void} onGraphUnmounted
   */
  constructor(onGraphMounted: () => void, onGraphUnmounted: () => void) {
    super(onGraphMounted, onGraphUnmounted, new ESDMxGraphModel());
    this.branchKeys = new Set<string>();
  }

  /**
   * @override
   */
  protected setValidationRules() {
    this.cellRenderer.getLabelBounds = (state) => {
      var graph = state.view.graph;
      var isEdge = graph.getModel().isEdge(state.cell);
      if (!isEdge || state.cell instanceof EventTreeSuccessEdge) {
        return mxCellRenderer.prototype.getLabelBounds(state);
      }

      var scale = state.view.scale;
      var bounds = new mxRectangle(state.absoluteOffset.x - state.width / 2, state.absoluteOffset.y + state.height / 2);

      var spacing = state.text.getSpacing();
      bounds.x += spacing.x * scale;
      bounds.y += spacing.y * scale;

      var geo = graph.getCellGeometry(state.cell);

      if (geo != null) {
        bounds.width = Math.max(0, geo.width * scale);
        bounds.height = Math.max(0, geo.height * scale);
      }

      if (state.text.isPaintBoundsInverted()) {
        // Rotates around center of state
        var t = (state.width - state.height) / 2;
        bounds.x += t;
        bounds.y -= t;
        var tmp = bounds.width;
        bounds.width = bounds.height;
        bounds.height = tmp;
      }

      // Shape can modify its label bounds
      // Shape can modify its label bounds
      if (state.shape != null) {
        var hpos = mxUtils.getValue(state.style, mxConstants.STYLE_LABEL_POSITION, mxConstants.ALIGN_CENTER);
        var vpos = mxUtils.getValue(state.style, mxConstants.STYLE_VERTICAL_LABEL_POSITION, mxConstants.ALIGN_MIDDLE);

        if (hpos == mxConstants.ALIGN_CENTER && vpos == mxConstants.ALIGN_MIDDLE) {
          bounds = state.shape.getLabelBounds(bounds);
        }
      }

      // HCLText width style overrides actual label width
      var lw = mxUtils.getValue(state.style, mxConstants.STYLE_LABEL_WIDTH, null);

      if (lw != null) {
        bounds.width = parseFloat(lw) * scale;
      }

      //bounds.x = 0;
      return bounds;
    };
  }

  public createCellRenderer(): ESDMxCellRenderer {
    return new ESDMxCellRenderer();
  }

  public createSelectionModel(): ESDMxGraphSelectionModel {
    return new ESDMxGraphSelectionModel(this);
  }

  /**
   * @override
   */
  getConnectionHandler() {
    return new mxConnectionHandler(this, ((source: any, target: any, style: any) => {
      const edge = new mxCell('', new mxGeometry());
      edge.setEdge(true);
      edge.setStyle(style);
      edge.geometry.relative = true;
      return edge;
    }));
  };

  /**
   * @override
   * @param {MxGraphType.mxCellState} state
   * @return {MxGraphType.mxRectangle}
   */
  getVertexControlBoundsRulesForCellState(state: mxCellState): mxRectangle {
    //@ts-ignore
    if (state.control != null) {
      //@ts-ignore
      const oldScale = state.control.scale;
      //@ts-ignore
      const w = state.control.bounds.width / oldScale;
      //@ts-ignore
      const h = state.control.bounds.height / oldScale;
      const s = state.view.scale;
      return new mxRectangle(state.x + state.width / 2 - w / 2 * s + 0.5, state.y + state.height - h / 2 * s + 7, w * s, h * s);
    }
    return null;
  }

  public generateUniqueBranchKey(): string {
    let key;
    do {
      key = HCLTreeMxGraph.generateShortID();
    } while (this.branchKeys.has(key));
    return key;
  }

  addVertex(vertex: HCLTreeVertex, parent: mxCell = this.getDefaultParent()): HCLTreeVertex {
    const cell = super.addVertex(vertex, parent);
    if (cell instanceof FunctionalEventVertex) {
      const value = cell.getValue() as FunctionalEventVertexValue;
      const branchKey = value.getBranchKey();
      if (branchKey) {
        this.branchKeys.add(branchKey);
      } else {
        const key = this.generateUniqueBranchKey();
        value.setBranchKey(key);
        this.branchKeys.add(key);
      }
    }
    return cell;
  }

  removeVertex(vertex: HCLTreeVertex, removeEdges: boolean = true): mxCell[] {
    const cells = super.removeVertex(vertex, removeEdges);
    if (cells) {
      cells
        .filter(cell => cell instanceof FunctionalEventVertex)
        .forEach(cell => {
          this.branchKeys.delete((cell.getValue() as FunctionalEventVertexValue).getBranchKey());
      });
    }

    return cells;
  }

  removeAllVertices(): mxCell[] {
    const cells = super.removeAllVertices();
    if (cells) this.branchKeys = new Set();
    return cells;
  }

  /**
   * @override
   * @param {MxGraphType.mxCell} cell
   * @param {boolean} show
   */
  toggleSubTree(cell: mxCell, show: boolean): void {
    const cells: mxCell[] = [];
    // @ts-ignore
    this.traverse(cell, true, (vertex) => {
      if (vertex !== cell) {
        cells.push(vertex);
      }
      // Stops recursion if a collapsed cell is seen
      return vertex === cell || !this.isCellCollapsed(vertex);
    });
    this.toggleCells((show != null) ? show : true, cells, true);
  }

  /**
   * @override
   */
  onNotifyGraphMounted(): void { }

  /**
   * @override
   */
  onMountComplete(): void { }

  /**
   * @override
   */
  onUnmountComplete(): void { }

  /**
   * @override
   */
  getDefaultLayout(): mxGraphLayout {
    //@ts-ignore
    return new ESDMxGraphLayout(this);
  }

  /**
   * @param {EventTreeMxGraphJSON} inputJSON
   */
  populateFromJSON(inputJSON: EventTreeMxGraphJSON): void {
    new EventTreeMxGraphDecoder(this, inputJSON).decode();
  }

  /**
   * @override
   * @return {EventTreeMxGraphJSON}
   */
  toJSON(): EventTreeMxGraphJSON {
    return new EventTreeMxGraphEncoder(this).encode();
  }

  /**
   * @override
   */
  setStyles(): void { }

  public isValidSource(cell: any): boolean {
    return super.isValidSource(cell) && !(cell instanceof EndStateVertex || cell instanceof FunctionalEventVertex);
  }

  public isValidTarget(cell: any): boolean {
    return super.isValidTarget(cell) && !(cell instanceof InitVertex || cell instanceof FunctionalEventPort);
  }

  /**
   *  @return {@link ESDMxConnectionHandler}
   */
  createConnectionHandler(): ESDMxConnectionHandler {
    return new ESDMxConnectionHandler(this);
  }

  createStylesheet(): ESDMxStylesheet {
    return new ESDMxStylesheet();
  }

  public addVertexWithUniqueKey(vertex: HCLTreeVertex, parent: mxCell = this.getDefaultParent()): EventTreeVertex {
    if (vertex instanceof EventTreeVertex) {
      // @ts-ignore
      return super.addVertexWithUniqueKey(vertex, parent);
    }
    return null;
  }
}

export default EventTreeMxGraph;
export { EventTreeMxGraphJSON, BranchJSON };
